<template>
    <ValidationObserver
        ref="testCalculation"
        v-slot="{ passes}"
    >
        <BaseDialog
            :showDialog="showDialog"
            :headline="$t('lang_GENERIC_CAPTION_TEST_CALCULATION')"
            :close="closeDialog"
            maxDialogWidth="1500"
            maxCardHeight="800"
            :alvqa="alvqa"
        >
            <template v-slot:dialogText>
                <v-container fluid>
                    <v-row class="pl-3 headline">
                        {{ getReleaseName() }}
                    </v-row>
                    <v-row>
                        <v-col>
                            <BaseEditPageDropdown 
                                :context="context"
                                field="Licenses"
                                :items="licensesItems" 
                                :label="$t('lang_GENERIC_LABEL_DROPDOWN_SALES_TYPE_LICENSE')"
                                :selection="true"
                                rules="required"
                                :withMargin="false"
                                :clearable="false"
                                :alvqa="alvqa + '-dropdown-licenses'"
                            />
                        </v-col>
                        <v-col>
                            <BaseEditPageDropdown 
                                :context="context"
                                field="SalesType"
                                :items="salesTypeItems" 
                                :label="$t('lang_LABEL_SALES_DATA_CAPTION_SALES_TYPE')"
                                :selection="true"
                                rules="required"
                                :withMargin="false"
                                :clearable="false"
                                :alvqa="alvqa + '-dropdown-sales-type'"
                            />
                        </v-col>
                    </v-row>
                    <v-row>
                        <v-col>
                            <BaseEditPageDropdown 
                                :context="context"
                                field="Country"
                                :items="countryItems" 
                                :label="$t('lang_PUBLISHING_WORKS_DIALOG_CAPTION_ORIGINATING_COUNTRY')"
                                :selection="true"
                                :withMargin="false"
                                :alvqa="alvqa + '-dropdown-country'"
                            />
                        </v-col>
                        <v-col>
                            <BaseEditPageTextField 
                                :context="context"
                                field="Net"
                                :label="$t('lang_GENERIC_LABEL_CAPTION_NET')"
                                :withMargin="false"
                                :rules="dynamicRules('net', 'amount')"
                                :alvqa="alvqa + '-textfield-net'"
                            /> 
                        </v-col>
                    </v-row>
                    <v-row>
                        <v-col>
                            <BaseEditPageTextField 
                                :context="context"
                                field="Quantity"
                                :label="$t('lang_GENERIC_CAPTION_QUANTITY')"
                                :withMargin="false"
                                :rules="dynamicRules('quantity', 'amount')"
                                :alvqa="alvqa + '-textfield-quantity'"
                            /> 
                        </v-col>
                        <v-col>
                            <BaseEditPageTextField 
                                :context="context"
                                field="Ppd"
                                :label="$t('lang_GENERIC_LABEL_CAPTION_PPD')"
                                :withMargin="false"
                                :rules="dynamicRules('ppd', 'amount')"
                                :alvqa="alvqa + '-textfield-ppd'"
                            /> 
                        </v-col>
                    </v-row>
                    <v-row>
                        <v-col>
                            <BaseEditPageTextField 
                                :context="context"
                                field="Isrc"
                                :label="$t('lang_GENERAL_CAPTION_ISRC')"
                                :withMargin="false"
                                :alvqa="alvqa + '-textfield-isrc'"
                            /> 
                        </v-col>
                        <v-col>
                            <BaseEditPageTextField 
                                :context="context"
                                field="Msp"
                                :label="$t('lang_GENERIC_LABEL_CAPTION_MSP')"
                                :withMargin="false"
                                :rules="dynamicRules('msp', 'amount')"
                                :alvqa="alvqa + '-textfield-msp'"
                            /> 
                        </v-col>
                    </v-row>
                    <v-divider />
                    <v-row v-if="errorMessage !== null" class="pt-3">
                        <v-col>
                            <v-chip  
                                small 
                                label
                                color="errorLineChip"
                                text-color="errorLineText"
                                class="mr-6"
                                :alvqa="alvqa + 'result-chip-error'"
                            >{{ $t('lang_GENERAL_CAPTION_ERROR') }}</v-chip>
                            {{  $t(errorMessage) }}
                            {{ errorMessage.split('_').pop()}}
                        </v-col>
                    </v-row>
                    <v-row v-for="(entry, sparte) in result" :key="'row_' + sparte" class="pt-3">
                        <v-col>
                            <v-chip 
                                v-for="(el, index) in sparte.split('#')"
                                :key="index"
                                label
                                class="mr-1"
                                color="resultHeadlineBackground" 
                                text-color="resultHeadlineText"
                                :alvqa="alvqa + '-result-list-chip-type-of-right-' + el.toLowerCase().replace(/\s+/g, '-')"
                            >{{ $t(el) }}</v-chip>
                            <v-row>
                                <v-col>
                                    <v-data-table
                                        v-if="entry && entry.length && entry[0].error === -1"
                                        :headers="resultHeaders"
                                        :items="entry"
                                        hide-default-footer
                                        disable-sort
                                        dense
                                        :items-per-page="itemsPerPage"
                                    >
                                        <template v-slot:item="{ item, index }">
                                            <tr>
                                                <td>
                                                    <v-chip  
                                                        small 
                                                        label
                                                        color="excelLineChip"
                                                        text-color="excelLineText"
                                                        :alvqa="alvqa + 'result-list-row-' + sparte.toLowerCase().replace(/\s+/g, '-') + '-chip-excel'"
                                                    >
                                                        <span class="mr-2 ml-1">{{ $t('lang_GENERAL_CAPTION_EXCEL') }}</span>
                                                    </v-chip>
                                                </td> 
                                                <td 
                                                    class="ellipsis"
                                                    :alvqa="alvqa + '-result-list-' + sparte.toLowerCase().replace(/\s+/g, '-') + '-row-' + (index+1) + '-text-statement-for'"
                                                ><BaseTooltip :simpleText="item.name" /></td>
                                                <td 
                                                    class="text-right"
                                                    :alvqa="alvqa + '-result-list-' + sparte.toLowerCase().replace(/\s+/g, '-') + '-row-' + (index+1) + '-text-original-amount'"
                                                >
                                                  {{ $alvPlugin.getNumberLocalization(item.originalAmount , 'number') }}
                                                </td>
                                                <td 
                                                    class="text-right"
                                                    :alvqa="alvqa + '-result-list-' + sparte.toLowerCase().replace(/\s+/g, '-') + '-row-' + (index+1) + '-text-base-percent'"
                                                > {{ $alvPlugin.getNumberLocalization(item.basePercent, 'number') }}
                                                </td>
                                                <td 
                                                    class="text-right"
                                                    :alvqa="alvqa + '-result-list-' + sparte.toLowerCase().replace(/\s+/g, '-') + '-row-' + (index+1) + '-text-share-percent'"
                                                > {{ $alvPlugin.getNumberLocalization(item.sharePercent, 'number') }}
                                                </td>
                                                <td 
                                                    class="text-right"
                                                    :alvqa="alvqa + '-result-list-' + sparte.toLowerCase().replace(/\s+/g, '-') + '-row-' + (index+1) + '-text-share-amount'"
                                                > {{ $alvPlugin.getNumberLocalization(item.shareAmount, 'number') }}
                                                </td>
                                                <td 
                                                    class="no_ellipsis"
                                                    :alvqa="alvqa + '-result-list-' + sparte.toLowerCase().replace(/\s+/g, '-') + '-row-' + (index+1) + '-text-contract-alv-id'"
                                                >
                                                {{ item.contractAlvId  }}</td>
                                                <td 
                                                    class="no_ellipsis"
                                                    :alvqa="alvqa + '-result-list-' + sparte.toLowerCase().replace(/\s+/g, '-') + '-row-' + (index+1) + '-text-contract-type'"
                                                >
                                                {{$t(item.contractType)}}</td>
                                            </tr>
                                            <template v-if="item.rowInfo !== undefined">
                                            <!-- <tr v-for="(rowInfo, key) in item.rowInfo" :key="'row_info_' + key + index" class="pt-3" > -->
                                            <tr class="pt-3" >
                                                <td></td>
                                                <td colspan="8" :alvqa="alvqa + 'result-list-row-' + sparte.toLowerCase().replace(/\s+/g, '-') + '-contract-line'">
                                                        <v-chip  
                                                            small 
                                                            label
                                                            color="contractLineChip"
                                                            class="mr-6"
                                                            :alvqa="alvqa + 'result-list-row-' + sparte.toLowerCase().replace(/\s+/g, '-') + '-chip-contract'"
                                                        >
                                                        {{ $t('lang_PUBLISHING_WORKS_SINGLE_TEST_CONTRACT_ROW_CAPTION') }}</v-chip> 
                                                        {{ $t('lang_GENERIC_CAPTION_SALES_TYPE') }}: {{$t(item.rowInfo.salesType) }} | 
                                                        {{ $t('lang_GENERIC_LABEL_DROPDOWN_SALES_TYPE_LICENSE') }}: {{$t(item.rowInfo.license) }} | 
                                                        {{ $t('lang_PUBLISHING_WORKS_DIALOG_CAPTION_SHARE') }}: {{ item.rowInfo.contractShare !== '' ? `${$n(item.rowInfo.contractShare, 'number')} % ` : '--' }} | 
                                                        {{ $t('lang_PUBLISHING_WORKS_DIALOG_CAPTION_BASE') }}: {{ item.rowInfo.contractBase !== '' ? `${$n(item.rowInfo.contractBase, 'number')} % ` : '--' }} | 
                                                        {{ $t('lang_PUBLISHING_WORKS_DIALOG_CAPTION_REGION') }}: {{ item.rowInfo.contractRegion }} | 
                                                        {{ $t('lang_GENERIC_CAPTION_CATEGORY') }}: {{ item.rowInfo.contractCategory }}
                                                </td>
                                            </tr>
                                            </template>
                                        </template>
                                    </v-data-table>
                                    <div v-else-if="entry.error === 1" class="pt-3">
                                        <v-chip  
                                            small 
                                            label
                                            color="errorLineChip"
                                            text-color="errorLineText"
                                            class="mr-6"
                                            :alvqa="alvqa + '-result-chip-error'"
                                        >{{ $t('lang_GENERAL_CAPTION_ERROR') }}</v-chip>
                                        <span :alvqa="alvqa + '-result-error-text'">
                                            {{ entry[0].rowInfo.error }}
                                        </span>
                                    </div>
                                </v-col> 
                            </v-row>
                        </v-col>
                    </v-row>
                </v-container>
            </template>
            <template v-slot:dialogActions>
                <v-container fluid>
                    <v-row>
                        <v-spacer />
                        <v-col class="text-right">
                            <BaseButton 
                                :disabled="disableCalculateButton"
                                label="lang_GENERAL_CAPTION_CALCULATE"
                                @click="passes(calculate)"
                                :alvqa="alvqa+'-button-calculate'"
                            />
                        </v-col>
                    </v-row>
                </v-container>
            </template>
        </BaseDialog>
    </ValidationObserver>
</template>

<script>
import BaseDialog from '@/components/_base/BaseDialog'
import BaseButton from '@/components/_buttons/BaseButton'
import BaseEditPageTextField from '@/components/_editForm/BaseEditPageTextField'
import BaseEditPageDropdown from '@/components/_editForm/BaseEditPageDropdown'
import BaseEditPageAutocomplete from '@/components/_editForm/BaseEditPageAutocomplete'
import BaseTooltip from '@/components/_base/BaseTooltip'

import { DropdownLabelReleasesLicensesOptions, DropdownLabelReleasesSalesTypeOptions } from '@/assets/dropdowns/alvDropdowns.js'

export default {
    name: "TestCalculationReleaseDialog",
    components: {
        BaseDialog,
        BaseButton,
        BaseEditPageTextField,
        BaseEditPageDropdown,
        BaseEditPageAutocomplete,
        BaseTooltip
    },
    data() {
        return {
            context: "TestCalculationRelease",
            alvqa: 'label-release-dialog-test-calculation',
            disableCalculateButton: false
        }
    },
    computed: {
        itemsPerPage() { return this.$store.getters.getItemsPerPage },

        // Dropdowns
        licensesItems() { 
            let licensesArr
            let licenses = DropdownLabelReleasesLicensesOptions

            if(licenses !== undefined) {
                licensesArr = JSON.parse(JSON.stringify(licenses))
                licensesArr.forEach(element => {
                    element['alvqa'] = this.alvqa + '-dropdown-licenses-' + element.value
                });
            }

            return licensesArr
        },
        salesTypeItems() { 
            let salesTypeArr
            let salesType = DropdownLabelReleasesSalesTypeOptions

            if(salesType !== undefined) {
                salesTypeArr = JSON.parse(JSON.stringify(salesType))
                salesTypeArr.forEach(element => {
                    element['alvqa'] = this.alvqa + '-dropdown-sales-type-' + element.value
                });
            }

            return salesTypeArr
        },

        countryItems() { return this.$alvPlugin.getListOfTranslatedCountries() },

        result: {
            get() { return this.$store.getters.getLabelReleaseTestCalculationResult},
            set(result) { this.$store.commit('setLabelReleaseTestCalculationResult', result) }
        } ,
        errorMessage: { 
            get() { return this.$store.getters.getLabelReleaseTestCalculationErrorMessage },
            set(msg) { this.$store.commit('setLabelReleaseTestCalculationErrorMessage', msg) }
        },

        showDialog: {
            get() { return this.$store.getters.getLabelTestReleasesDialog },
            set(show) { this.$store.commit('setLabelTestReleasesDialog', show) }
        },

        resultHeaders() {
            return [
                {
					text: '', 
					value: 'contractLine', 
                    align: 'center',
                    width: "95px",
                    sortable: false,
					class: 'text-no-wrap'
                },
				{
					text: this.$t('lang_PUBLISHING_WORKS_DIALOG_CAPTION_STATEMENT_FOR'), 
					value: 'name', 
					align: 'left',
                    sortable: false,
					class: 'text-no-wrap'
                },
				{
					text: this.$t('lang_PUBLISHING_WORKS_DIALOG_CAPTION_AMOUNT') + ' ' + this.$store.getters.getSelectedCurrency,
					value: 'originalAmount', 
                    align: 'right', 
                    width: "140px",
                    sortable: false,
					class: 'text-no-wrap'
                }, 
				{
					text: this.$t('lang_PUBLISHING_WORKS_DIALOG_CAPTION_BASE') + ' %',
					value: 'basePercent', 
                    align: 'right', 
                    width: "140px",
                    sortable: false,
					class: 'text-no-wrap'
                }, 
				{
					text: this.$t('lang_PUBLISHING_WORKS_DIALOG_CAPTION_SHARE') + ' %',
					value: 'sharePercent ', 
                    align: 'right', 
                    width: "140px",
                    sortable: false,
					class: 'text-no-wrap'
                }, 
				{
					text: this.$t('lang_PUBLISHING_WORKS_DIALOG_CAPTION_SHARE') + ' ' + this.$store.getters.getSelectedCurrency,
					value: 'shareAmount', 
                    align: 'right', 
                    width: "140px",
                    sortable: false,
					class: 'text-no-wrap'
                },
				{
					text: this.$t('lang_LABELCONTRACT_PLACEHOLDER'), 
					value: 'contractAlvId', 
                    align: 'left',
                    width: "200px",
                    sortable: false,
					class: 'text-no-wrap'
                },
				{
					text: this.$t('lang_GENERIC_CONTRACTS_CAPTION_CONTRACT_TYPE'), 
					value: 'contractType', 
                    align: 'left',
                    width: "140px",
                    sortable: false,
					class: 'text-no-wrap'
                }
            ]
        }
    },
    methods: {
        dynamicRules(type, defaultRule = '') {
            const selectedLicenses = this.$store.getters.getTestCalculationReleaseDetailsLicenses;

            if (selectedLicenses === 'net' && type == 'net') {
                return defaultRule ? `${defaultRule}|required` : 'required';
            } else if (selectedLicenses === 'stck' && type == 'quantity') {
                return defaultRule ? `${defaultRule}|required` : 'required';
            } else if (selectedLicenses === 'ppd' && type == 'ppd') {
                return defaultRule ? `${defaultRule}|required` : 'required';
            } else if (selectedLicenses === 'msp' && type == 'msp') {
                return defaultRule ? `${defaultRule}|required` : 'required';
            }

            return defaultRule;
        },
        closeDialog() {
            this.showDialog = false
            this.$store.commit('setSelectedLabelReleasesAction', [])
            this.$store.commit('setSelectedLabelReleases', [])
            this.$store.commit('setIsAllSelectedLabelReleases', false)
            this.result = []
            this.errorMessage = null
        },
        calculate() {
            this.result = []
            this.errorMessage = null
            this.disableCalculateButton = true
            this.$store.dispatch('calculateLabelReleaseTestCalculation').then(() => {
            this.disableCalculateButton = false
            })
        },
        getReleaseName() {
            let selectedLabelAlvId = this.$store.getters.getSelectedLabelReleases
            let labels = this.$store.getters.getLabelReleases
            let title = ''

            let match = labels.find(label => label.alvId === selectedLabelAlvId[0])
            if(match !== undefined) {
                title += `${match.title} (${match.alvId})`
            }
            return title
        }
    },
    created() {
        this.$store.commit('setCountries')
    },
}
</script>